import type { CartCategoryOption } from "@/utils/cart";
import { Button } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { X } from "lucide-react";
import { memo } from "react";
import { CartStatsGrid } from "./CartStatsGrid";
import { CategoryFilterList } from "./CategoryFilterList";
import { SupplierFilterList } from "./SupplierFilterList";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;
type StoreSellerCartsSummary = SalonStockerHttpTypes.StoreSellerCartsSummary;

export interface CartFilterSidebarProps {
  /**
   * All available seller carts
   */
  carts: StoreSellerCart[];
  /**
   * IDs of currently selected carts
   */
  selectedCartIds: string[];
  /**
   * Callback when toggling a cart's selection
   */
  onToggleCart: (cartId: string) => void;
  /**
   * Callback to select all carts
   */
  onSelectAll: () => void;
  /**
   * Whether all carts are selected
   */
  isAllSelected: boolean;
  /**
   * Summary of all carts (unfiltered)
   */
  totalSummary: StoreSellerCartsSummary;
  /**
   * Summary of selected carts only (filtered)
   */
  filteredSummary: StoreSellerCartsSummary;
  /**
   * Categories represented by the products in the cart
   */
  categories: CartCategoryOption[];
  /**
   * IDs of currently selected categories. Defaults to every category (all
   * ticked), matching how the supplier list starts fully selected.
   */
  selectedCategoryIds: string[];
  /**
   * Callback when toggling a category's selection
   */
  onToggleCategory: (categoryId: string) => void;
  /**
   * Callback to re-select every category - "clear" here means "stop
   * restricting", not deselect-all. Unticking everything would leave the
   * list showing no ticked boxes while every product stayed visible (an
   * empty selection reads as "no filter" downstream), which looks broken.
   */
  onClearCategories: () => void;
  /**
   * Whether every category is currently selected
   */
  isAllCategoriesSelected: boolean;
  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Left sidebar component for filtering seller carts.
 *
 * Features:
 * - Displays cart summary statistics
 * - Multi-select checkboxes for filtering by seller
 * - Reset filters button
 *
 * @example
 * ```tsx
 * <CartFilterSidebar
 *   carts={allCarts}
 *   selectedCartIds={selectedIds}
 *   onToggleCart={toggleCart}
 *   onSelectAll={selectAll}
 *   isAllSelected={isAllSelected}
 *   totalSummary={totalSummary}
 *   filteredSummary={filteredSummary}
 * />
 * ```
 */
function CartFilterSidebarComponent({
  carts,
  selectedCartIds,
  onToggleCart,
  onSelectAll,
  isAllSelected,
  totalSummary,
  filteredSummary,
  categories,
  selectedCategoryIds,
  onToggleCategory,
  onClearCategories,
  isAllCategoriesSelected,
  className
}: CartFilterSidebarProps) {
  const hasAnyFilter = !isAllSelected || !isAllCategoriesSelected;

  // "Clear" means "stop filtering": tick every supplier and category back on.
  // Deselecting suppliers here would empty the cart and read as a bug.
  const handleClearAll = () => {
    onSelectAll();
    onClearCategories();
  };

  return (
    <div role="region" aria-label="Cart filter" className={className}>
      {/* Cart Statistics - kept above the filters, unlike search, which has
          no equivalent summary to show. */}
      <CartStatsGrid
        summary={filteredSummary}
        totalSellers={totalSummary.total_sellers}
      />

      {/* Header mirrors the search page's filter rail: a "Filters" heading
          with a transparent Clear action on the right. */}
      <div className="mb-4 mt-6 flex items-center justify-between">
        <h3 className="font-semibold">Filters</h3>
        {hasAnyFilter && (
          <Button
            size="small"
            variant="transparent"
            onClick={handleClearAll}
            aria-label="Reset all filters"
          >
            <X className="h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      <SupplierFilterList
        carts={carts}
        selectedCartIds={selectedCartIds}
        onToggleCart={onToggleCart}
      />

      {/* <CategoryFilterList
        categories={categories}
        selectedCategoryIds={selectedCategoryIds}
        onToggleCategory={onToggleCategory}
      /> */}
    </div>
  );
}

export const CartFilterSidebar = memo(CartFilterSidebarComponent);
